import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import { skip, take } from 'lib/array/arrayUtils'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FilterPanelCheckboxGroupItem from './FilterPanelCheckboxGroupItem'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { useIntl } from 'react-intl'

const CheckboxGroup = styled(VerticalSpacer)`
  margin-top: ${rem(6)};
`

export interface FilterPanelCheckItem {
  label: React.ReactNode
  value: number | string
  defaultChecked?: boolean
  checked?: boolean
  hidden?: boolean
  price?: number
  count?: number
  enabled?: boolean
  hideCount?: boolean
  hideCheckbox?: boolean
}

interface Props {
  name: string
  items: Array<FilterPanelCheckItem>
  defaultSelected?: Set<string | number>
  selected?: Set<string | number>
  maxItems?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  titlecase?: boolean
  allowShowMore?: boolean
  allowCheckAll?: boolean
  loadingValues?: boolean
  maxValue?: number
}

function FilterPanelCheckboxGroup(props: Props) {
  const {
    onChange,
    name,
    items,
    maxItems = Infinity,
    defaultSelected,
    selected,
    titlecase = false,
    allowShowMore = true,
    allowCheckAll = false,
    loadingValues,
    maxValue = 1000,
  } = props
  const intl = useIntl()
  const [showMore, toggleShowMore] = useToggle(!allowShowMore)
  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])
  const hasMore = visibleItems.length > maxItems

  // check all items from visibleItems, also uncheck all items from visibleItems
  const onCheckAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked

    const values = new Set(visibleItems.map(item => item.value))

    onChange?.({
      ...e,
      target: {
        ...e.target,
        name,
        value: checked ? Array.from(values).join(',') : '',
      },
    })
  }, [name, onChange, visibleItems])

  const allLabel = intl.formatMessage({
    id: '4AnY0LVHfJOfBTaRm8Sz',
    defaultMessage: 'All',
    description: 'Checkbox label to select all filter options in the filters panel',
  })
  const showMoreLabel = showMore
    ? intl.formatMessage({
        id: 'pwKWEC255CCTQ95WgCu8',
        defaultMessage: 'Show less',
        description: 'Toggle button label to collapse and show fewer filter options',
      })
    : intl.formatMessage({
        id: '23D7PyGMWMhoe37SIUVQ',
        defaultMessage: 'Show more',
        description: 'Toggle button label to expand and show more filter options',
      })

  return (
    <CheckboxGroup gap={8}>
      {allowCheckAll && <CheckboxInput
        key="checkAll"
        name="All"
        value="All"
        size="medium"
        onChange={onCheckAll}
        checked={visibleItems.every(item => item.checked)}
      >
        {allLabel}
      </CheckboxInput>}
      {take(visibleItems, maxItems).map(item =>
        <FilterPanelCheckboxGroupItem
          key={item.value}
          item={item}
          name={name}
          onChange={onChange}
          defaultChecked={item.defaultChecked ?? defaultSelected?.has(item.value)}
          checked={item.checked ?? selected?.has(item.value)}
          titlecase={titlecase}
          loadingValue={loadingValues}
          hideCheckbox={item.hideCheckbox}
          maxValue={maxValue}
        />,
      )}
      {hasMore && <>
        <Expander expanded={showMore}>
          <VerticalSpacer gap={8}>
            {skip(visibleItems, maxItems).map(item =>
              <FilterPanelCheckboxGroupItem
                key={item.value}
                item={item}
                name={name}
                onChange={onChange}
                defaultChecked={item.defaultChecked ?? defaultSelected?.has(item.value)}
                checked={item.checked ?? selected?.has(item.value)}
                titlecase={titlecase}
                loadingValue={loadingValues}
                hideCheckbox={item.hideCheckbox}
                maxValue={maxValue}
              />,
            )}
          </VerticalSpacer>
        </Expander>
        {allowShowMore && <SmallToggleButton
          on={showMore}
          onClick={toggleShowMore}
          size="medium"
          iconPosition="start"
        >
          {showMoreLabel}
        </SmallToggleButton>}
      </>}
    </CheckboxGroup>

  )
}

export default React.memo(FilterPanelCheckboxGroup)
